import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  ButtonGroupItem,
  Breadcrumb,
  Modal,
  ModalHeading,
  ModalBody,
  ModalFooter,
} from '@boozallen-uip/uswds-react-core';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import useEntity from '../../hooks/useEntity.hook';
import SearchableSelect from '../../components/SearchableSelect.component';
import {
  BASE_TITLE,
  ENTITY_TYPE_AGENCY,
  ENTITY_TYPE_VENDOR,
  HOME_URL,
  SUPERUSER_TOOLS_URL,
  VENDOR_OVERVIEW_URL,
} from '../../utlities/core.constants';
import { PageSubHeader, SectionContainer, SectionSubHeader } from '../shared.styled';
import PageHeader from '../../components/PageHeader/PageHeader.component';
import './SuperUserTools.page.css';

const WIZARD_ID = 'support-mode-wizard';
const TOTAL_STEPS = 3;

/**
 * SuperUser Tools: a hub for SuperUser-only utilities. Today it hosts Support Mode, which lets a
 * SuperUser step into an organization's context (Vendor or Buyer/Agency) to view, create, and edit
 * its products, ACRs, and details as if an admin of that org. The real SuperUser identity is
 * preserved server-side for audit; a persistent overlay marks the active support session.
 */
export default function SuperUserTools() {
  const { realIsSuperUser, realUser, startImpersonation } = useContext(UserContext);
  const { getVendors, getAgencies } = useEntity();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = Boolean(location.state?.impersonationExpired);

  const [orgType, setOrgType] = useState(ENTITY_TYPE_VENDOR);
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState(null);

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  document.title = `${BASE_TITLE} - Superuser Tools`;

  // Only real SuperUsers may reach this tool.
  useEffect(() => {
    if (!realIsSuperUser) {
      navigate(HOME_URL, { replace: true });
    }
  }, [realIsSuperUser, navigate]);

  // Load the org list for the selected type, keeping only active orgs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingOrgs(true);
      setError(null);
      setSelectedOrgId('');
      const data = orgType === ENTITY_TYPE_VENDOR ? await getVendors() : await getAgencies();
      if (cancelled) {
        return;
      }
      // Impersonating your own org is meaningless, so drop it from the list for the matching type.
      const ownOrgId = orgType === ENTITY_TYPE_VENDOR ? realUser?.vendorId : realUser?.agencyId;
      const list = Array.isArray(data) ? data : [];
      setOrgOptions(
        list
          .filter((org) => org && org.id && org.isActive !== false && org.id !== ownOrgId)
          .map((org) => ({ value: org.id, label: org.name || org.id }))
      );
      setIsLoadingOrgs(false);
    })();
    return () => { cancelled = true; };
  }, [orgType, getVendors, getAgencies, realUser?.vendorId, realUser?.agencyId]);

  const sortedOptions = useMemo(
    () => [...orgOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [orgOptions]
  );

  const orgTypeNoun = orgType === ENTITY_TYPE_VENDOR ? 'vendor' : 'agency';

  const openWizard = () => {
    setError(null);
    setWizardStep(1);
    setIsWizardOpen(true);
  };

  const closeWizard = () => {
    setIsWizardOpen(false);
    setWizardStep(1);
  };

  const handleWizardStateChanged = (nextState) => {
    if (typeof nextState === 'boolean' && !nextState) {
      closeWizard();
    }
  };

  const goToNextStep = () => setWizardStep((step) => Math.min(step + 1, TOTAL_STEPS));
  const goToPreviousStep = () => setWizardStep((step) => Math.max(step - 1, 1));

  const onGo = async () => {
    if (!selectedOrgId) {
      return;
    }
    setIsStarting(true);
    setError(null);
    const result = await startImpersonation(orgType, selectedOrgId);
    setIsStarting(false);
    if (result.success) {
      // Land on the org's "My Product Listings" (overview) rather than the deprecated Vendor Dashboard.
      navigate(VENDOR_OVERVIEW_URL);
    } else {
      setError('Could not start Support Mode. The organization may be inactive or unavailable.');
    }
  };

  if (!realIsSuperUser) {
    return <section />;
  }

  return (
    <>
    <PageHeader imageUrl="/page-banners/account_hero_2x.webp" backgroundSize="2800px 391px" className="2xl:!bg-cover">
      Superuser Tools
    </PageHeader>
    <SectionContainer className="pt-4">
      <Breadcrumb links={[{ href: HOME_URL, text: 'Home' }, { href: SUPERUSER_TOOLS_URL, text: 'Superuser Tools' }]} />
      <PageSubHeader>Superuser Tools</PageSubHeader>

      {sessionExpired ? (
        <p className="text-red mb-4" role="status">
          Your Support Mode session ended. Start Support Mode again to begin a new one.
        </p>
      ) : null}

      <section className="mt-6 max-w-2xl">
        <SectionSubHeader>Support Mode</SectionSubHeader>
        <p className="mb-4">
          Step into an organization to act on its behalf. You will be able to view, create, and edit
          that organization&apos;s products, ACRs, and details. A banner will remind you that you are
          in Support Mode, and all changes are attributed to you for audit.
        </p>
        <Button type="button" onClick={openWizard}>Begin</Button>
      </section>

      <Modal
        id={WIZARD_ID}
        labelledBy={`${WIZARD_ID}-heading`}
        describedBy={`${WIZARD_ID}-body`}
        isOpen={isWizardOpen}
        onStateChanged={handleWizardStateChanged}
      >
        <ModalHeading id={`${WIZARD_ID}-heading`}>Support Mode</ModalHeading>

        <ModalBody id={`${WIZARD_ID}-body`}>
          <div
            className="support-wizard__progress"
            role="progressbar"
            aria-valuenow={wizardStep}
            aria-valuemin={1}
            aria-valuemax={TOTAL_STEPS}
            aria-label={`Step ${wizardStep} of ${TOTAL_STEPS}`}
          >
            {Array.from({ length: TOTAL_STEPS }, (_, index) => (
              <span
                key={index}
                className={`support-wizard__progress-dot${index < wizardStep ? ' support-wizard__progress-dot--active' : ''}`}
              />
            ))}
          </div>
          <p className="text-base-dark mb-2">Step {wizardStep} of {TOTAL_STEPS}</p>

          {wizardStep === 1 ? (
            <div key="step-1" className="support-wizard__step">
              <h3 className="font-bold text-lg mb-3">What organization type are you supporting?</h3>
              <fieldset className="border-0 p-0 m-0">
                <legend className="sr-only">Organization type</legend>
                <div className="flex gap-4">
                  <label htmlFor="orgType-vendor" className="flex items-center gap-2">
                    <input
                      id="orgType-vendor"
                      type="radio"
                      name="orgType"
                      value={ENTITY_TYPE_VENDOR}
                      checked={orgType === ENTITY_TYPE_VENDOR}
                      onChange={() => setOrgType(ENTITY_TYPE_VENDOR)}
                    />
                    Vendor
                  </label>
                  <label htmlFor="orgType-agency" className="flex items-center gap-2">
                    <input
                      id="orgType-agency"
                      type="radio"
                      name="orgType"
                      value={ENTITY_TYPE_AGENCY}
                      checked={orgType === ENTITY_TYPE_AGENCY}
                      onChange={() => setOrgType(ENTITY_TYPE_AGENCY)}
                    />
                    Agency (Buyer)
                  </label>
                </div>
              </fieldset>
            </div>
          ) : null}

          {wizardStep === 2 ? (
            <div key="step-2" className="support-wizard__step">
              <h3 className="font-bold text-lg mb-3">
                Choose the {orgTypeNoun} you want to become
              </h3>
              <label id="org-select-label" htmlFor="org-select" className="font-bold block mb-2">
                {orgType === ENTITY_TYPE_VENDOR ? 'Vendor' : 'Agency'}
              </label>
              <SearchableSelect
                id="org-select"
                options={sortedOptions}
                value={selectedOrgId}
                onChange={setSelectedOrgId}
                placeholder={isLoadingOrgs ? 'Loading organizations...' : 'Select an organization...'}
                filterPlaceholder="Type to filter organizations..."
                noResultsText="No organizations found"
                listAriaLabel="Organizations"
                disabled={isLoadingOrgs}
                fullWidth
              />
            </div>
          ) : null}

          {wizardStep === 3 ? (
            <div key="step-3" className="support-wizard__step">
              <p className="mb-4">
                Changes made in Support Mode are subject to audit. Support Mode sessions are limited to
                1 hour each, but you can exit anytime using the &quot;Exit Support Mode&quot; button.
                Press GO to enter Support Mode now.
              </p>
              {error ? (
                <p className="text-red" role="alert">{error}</p>
              ) : null}
            </div>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            {wizardStep === 1 ? (
              <ButtonGroupItem>
                <Button type="button" outline onClick={closeWizard}>Cancel</Button>
              </ButtonGroupItem>
            ) : (
              <ButtonGroupItem>
                <Button type="button" outline onClick={goToPreviousStep}>Back</Button>
              </ButtonGroupItem>
            )}

            {wizardStep < TOTAL_STEPS ? (
              <ButtonGroupItem>
                <Button
                  type="button"
                  onClick={goToNextStep}
                  disabled={wizardStep === 2 && !selectedOrgId}
                >
                  Next
                </Button>
              </ButtonGroupItem>
            ) : (
              <ButtonGroupItem>
                <Button type="button" onClick={onGo} disabled={!selectedOrgId || isStarting}>
                  {isStarting ? 'Starting...' : 'GO'}
                </Button>
              </ButtonGroupItem>
            )}
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </SectionContainer>
    </>
  );
}
